export const REQUEST_STATUSES = [
  { value: 'all', label: 'All statuses' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

export const TIME_FILTERS = [
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
];

const TERMINAL_STATUSES = new Set(['failed', 'cancelled', 'canceled', 'expired', 'refunded']);
const PREPAYMENT_STATUSES = new Set(['payment_pending', 'order_created']);
const PROCESSING_STATUSES = new Set(['payment_received', 'payout_pending']);

export function getCreatedFrom(period, now = new Date()) {
  const filter = TIME_FILTERS.find((item) => item.value === period) || TIME_FILTERS[0];
  if (filter.days) {
    return new Date(now.getTime() - filter.days * 24 * 60 * 60 * 1000).toISOString();
  }
  if (filter.value === 'year') {
    return new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString();
  }
  return undefined;
}

export function formatRequestDate(value, options = {}) {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: options.compact ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatAmount(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric)
    ? numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';
}

function legacyReadableStatus(status) {
  const labels = {
    payment_pending: 'KYC Pending',
    order_created: 'Payment Pending',
    payment_received: 'Transfer Processing',
    processing: 'Transfer Processing',
    payout_pending: 'Transfer Processing',
    completed: 'Provider Completed',
    kivi_completed: 'Transfer Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    refunded: 'Refunded',
    expired: 'Expired',
    not_started: 'Not Started',
  };
  return labels[status] || status;
}

function requestProjection(items) {
  const statuses = items.map((item) => String(item.status || '').toLowerCase());
  if (statuses.includes('kivi_completed')) return { status: 'completed', payoutStatus: 'completed' };
  if (statuses.includes('completed')) return { status: 'completed', payoutStatus: 'processing' };
  if (items.some((item) => {
    const status = String(item.status || '').toLowerCase();
    return PROCESSING_STATUSES.has(status) || (status === 'processing' && Boolean(item.payment_succeeded_at));
  })) {
    return { status: 'processing', payoutStatus: 'pending' };
  }
  if (statuses.some((status) => status !== 'not_started')) return { status: 'pending', payoutStatus: 'pending' };
  if (statuses.includes('not_started')) return { status: 'not_started', payoutStatus: 'not_started' };
  return { status: 'not_started', payoutStatus: 'not_started' };
}

function requestLabel(status) {
  return {
    not_started: 'Not Started',
    pending: 'Payment pending',
    processing: 'Processing',
    completed: 'Completed',
    expired: 'Expired',
  }[status] || status;
}

function payoutLabel(status) {
  return {
    not_started: 'Not started',
    pending: 'Waiting for payment',
    processing: 'Transfer processing',
    completed: 'Transfer complete',
    expired: 'Transfer expired',
  }[status] || status;
}

function transferOutcome(status, payoutStatus) {
  if (payoutStatus === 'completed') return 'Transfer complete';
  if (payoutStatus === 'processing') return 'Provider payment completed, payout processing';
  if (payoutStatus === 'expired') return 'Request expired before payout started';
  if (status === 'processing') return 'Payment received, provider transfer processing';
  return 'Waiting for one successful payment before payout starts';
}

export function groupLegacyTransactions(transactions = []) {
  const grouped = new Map();
  transactions.forEach((transaction) => {
    const requestId = transaction.request_id || transaction.remittance_transaction_id;
    if (!requestId) return;
    const list = grouped.get(requestId) || [];
    list.push(transaction);
    grouped.set(requestId, list);
  });

  return Array.from(grouped.entries()).map(([requestId, items]) => {
    const ordered = [...items].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const latest = ordered[ordered.length - 1];
    const projected = requestProjection(ordered);
    const active = [...ordered].reverse().find((item) => {
      const status = String(item.status || '').toLowerCase();
      return !TERMINAL_STATUSES.has(status) && status !== 'kivi_completed';
    });
    const attempts = ordered
      .filter((item) => String(item.status || '').toLowerCase() !== 'not_started')
      .map((item, index) => {
        const status = String(item.status || '').toLowerCase();
        const isActive = item.remittance_transaction_id === active?.remittance_transaction_id;
        return {
          attempt_id: item.remittance_transaction_id,
          sequence: index + 1,
          status,
          readable_status: item.readable_status || legacyReadableStatus(status),
          provider: item.partner_references?.sending_partner || 'TRANSAK',
          is_active: isActive,
          can_resume: isActive && PREPAYMENT_STATUSES.has(status),
          can_cancel: isActive && PREPAYMENT_STATUSES.has(status),
          blocks_new_attempt: isActive && !TERMINAL_STATUSES.has(status),
          created_at: item.created_at,
        };
      })
      .reverse();
    const latestStatus = String(latest.status || '').toLowerCase();
    const latestIsTerminal = TERMINAL_STATUSES.has(latestStatus);
    const canResume = attempts[0]?.can_resume === true;
    const canContinue = projected.status === 'not_started' || (projected.status === 'pending' && (canResume || latestIsTerminal));
    return {
      request_id: requestId,
      status: projected.status,
      readable_status: requestLabel(projected.status),
      payout_status: projected.payoutStatus,
      payout_readable_status: payoutLabel(projected.payoutStatus),
      transfer_outcome: transferOutcome(projected.status, projected.payoutStatus),
      created_at: ordered[0]?.created_at,
      link_token: latest.link_token,
      beneficiary: {
        full_name: latest.beneficiary?.full_name_receiver || 'Unknown receiver',
        phone: latest.receiver_phone,
        email: latest.receiver_email,
      },
      amount: {
        value: latest.transaction?.confirmed_crypto_amount || latest.transaction?.requested_amount_usd || 0,
        asset: latest.settlement?.asset || 'USDC',
        chain: latest.settlement?.chain || 'Polygon',
      },
      active_attempt_id: active?.remittance_transaction_id,
      available_actions: {
        continue: canContinue,
        continue_action: canContinue ? (canResume ? 'resume_attempt' : 'create_attempt') : 'none',
      },
      attempts,
    };
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export function filterPaymentRequests(requests, period, status, now = new Date()) {
  const createdFrom = getCreatedFrom(period, now);
  return requests.filter((request) => {
    if (status && status !== 'all' && request.status !== status) return false;
    return !createdFrom || new Date(request.created_at) >= new Date(createdFrom);
  });
}
